import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MysteryNumberArtifact } from "@seeing-maths/artifacts";
import type {
  ChoiceDefinition,
  JourneyDefinitionV1,
  ResumeCheckpoint,
  SolveDefinition,
  StageId,
} from "@seeing-maths/contracts";
import { STAGE_IDS } from "@seeing-maths/contracts";
import {
  classifyMissingPartAnswer,
  mysteryRelation,
  type MysteryNumberState,
} from "@seeing-maths/math-models";
import { FeedbackLane, StageRail } from "@seeing-maths/ui";
import { useLearning } from "../learning/learning-context";

type Tone = "quiet" | "attention" | "success" | "mismatch";

interface FeedbackState {
  tone: Tone;
  text: string;
}

interface MysterySession {
  value: number;
  playMoves: number;
  playComplete: boolean;
  noticeChoice: string | null;
  noticeCorrect: boolean;
  predictionChoice: string | null;
  predictionCorrect: boolean;
  named: boolean;
  solveIndex: number;
  solveAnswers: Record<string, string>;
  solveAttempts: Record<string, number>;
  solveCompleted: string[];
  hintLevels: Record<string, number>;
  explanationChoice: string | null;
  explanationCorrect: boolean;
  transferAnswer: string;
  transferAttempts: number;
  transferComplete: boolean;
  completed: boolean;
  remediation: string | null;
  feedback: FeedbackState;
}

const INITIAL_SESSION: MysterySession = {
  value: 2,
  playMoves: 0,
  playComplete: false,
  noticeChoice: null,
  noticeCorrect: false,
  predictionChoice: null,
  predictionCorrect: false,
  named: false,
  solveIndex: 0,
  solveAnswers: {},
  solveAttempts: {},
  solveCompleted: [],
  hintLevels: {},
  explanationChoice: null,
  explanationCorrect: false,
  transferAnswer: "",
  transferAttempts: 0,
  transferComplete: false,
  completed: false,
  remediation: null,
  feedback: {
    tone: "quiet",
    text: "Look closely. The artifact will show what your choices mean.",
  },
};

function restoreSession(value: Record<string, unknown> | undefined): MysterySession {
  if (!value) return structuredClone(INITIAL_SESSION);
  return {
    ...structuredClone(INITIAL_SESSION),
    ...value,
    solveAnswers: { ...INITIAL_SESSION.solveAnswers, ...(value.solveAnswers as object | undefined) },
    solveAttempts: { ...INITIAL_SESSION.solveAttempts, ...(value.solveAttempts as object | undefined) },
    hintLevels: { ...INITIAL_SESSION.hintLevels, ...(value.hintLevels as object | undefined) },
    solveCompleted: Array.isArray(value.solveCompleted) ? value.solveCompleted.map(String) : [],
    feedback:
      value.feedback && typeof value.feedback === "object"
        ? (value.feedback as FeedbackState)
        : INITIAL_SESSION.feedback,
  };
}

function ChoiceList({
  choices,
  selected,
  locked,
  onChoose,
}: {
  choices: ChoiceDefinition[];
  selected: string | null;
  locked: boolean;
  onChoose: (choice: ChoiceDefinition) => void;
}) {
  return (
    <div className="choice-list" role="group" aria-label="Choose one answer">
      {choices.map((choice) => (
        <button
          className={`choice-button ${selected === choice.id ? "is-selected" : ""}`}
          type="button"
          key={choice.id}
          aria-pressed={selected === choice.id}
          disabled={locked}
          onClick={() => onChoose(choice)}
        >
          <span className="choice-marker" aria-hidden="true" />
          <span>{choice.label}</span>
        </button>
      ))}
    </div>
  );
}

function stateForSolve(question: SolveDefinition, answer: string): MysteryNumberState {
  const parsed = Number(answer);
  return {
    known: question.known,
    target: question.target,
    coefficient: question.coefficient,
    unknown: Number.isFinite(parsed) && answer.trim() !== "" ? parsed : 0,
    min: -20,
    max: 30,
    locked: false,
  };
}

export function MysteryNumberJourney({
  definition,
  requestedStage,
}: {
  definition: JourneyDefinitionV1;
  requestedStage: StageId;
}) {
  const navigate = useNavigate();
  const { projection, emit, emitMany, saveCheckpoint } = useLearning();
  const concept = projection?.concepts[definition.id];
  const savedCheckpoint = concept?.resumeCheckpoint;
  const [session, setSession] = useState<MysterySession>(() =>
    restoreSession(savedCheckpoint?.session),
  );
  const sessionRef = useRef(session);
  const [stage, setStage] = useState<StageId>(() => requestedStage);
  const [maxStage, setMaxStage] = useState(() => savedCheckpoint?.maxStage ?? 0);
  const bootstrapped = useRef(false);
  const stageIndex = STAGE_IDS.indexOf(stage);
  const requestedIndex = STAGE_IDS.indexOf(requestedStage);

  const makeCheckpoint = (
    nextSession: MysterySession,
    nextStage: StageId,
    nextMax: number,
  ): ResumeCheckpoint => ({
    stage: nextStage,
    maxStage: nextMax,
    session: nextSession as unknown as Record<string, unknown>,
    savedAt: new Date().toISOString(),
  });

  const persist = async (
    nextSession: MysterySession,
    nextStage = stage,
    nextMax = maxStage,
  ) => {
    sessionRef.current = nextSession;
    setSession(nextSession);
    setStage(nextStage);
    setMaxStage(nextMax);
    await saveCheckpoint(definition.id, makeCheckpoint(nextSession, nextStage, nextMax));
  };

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void (async () => {
      if (!concept) {
        await emitMany(definition.id, [
          { type: "concept_started", payload: {} },
          { type: "stage_entered", payload: { stage: "notice" } },
        ]);
        const initial = { ...structuredClone(INITIAL_SESSION), value: definition.stages.play.start };
        await persist(initial, "notice", 0);
        if (requestedStage !== "notice") {
          navigate(`/journeys/${definition.id}/notice`, { replace: true });
        }
        return;
      }
      if (requestedIndex > maxStage) {
        const safeStage = STAGE_IDS[maxStage];
        setStage(safeStage);
        navigate(`/journeys/${definition.id}/${safeStage}`, { replace: true });
      } else if (requestedStage !== stage) {
        setStage(requestedStage);
        await saveCheckpoint(
          definition.id,
          makeCheckpoint(session, requestedStage, maxStage),
        );
      }
    })();
  }, []);

  useEffect(() => {
    if (!bootstrapped.current || requestedStage === stage || requestedIndex > maxStage) return;
    setStage(requestedStage);
  }, [requestedStage, requestedIndex, maxStage, stage]);

  const goToStage = async (nextStage: StageId) => {
    const nextIndex = STAGE_IDS.indexOf(nextStage);
    if (nextIndex > maxStage) return;
    const nextSession = {
      ...session,
      feedback: {
        tone: "quiet" as const,
        text: "You can revisit any stage you have already reached.",
      },
      remediation: null,
    };
    await emit(definition.id, { type: "stage_entered", payload: { stage: nextStage } });
    await persist(nextSession, nextStage, maxStage);
    navigate(`/journeys/${definition.id}/${nextStage}`);
  };

  const continueForward = async () => {
    const nextStage = STAGE_IDS[Math.min(STAGE_IDS.length - 1, stageIndex + 1)];
    const nextMax = Math.max(maxStage, stageIndex + 1);
    const nextSession = {
      ...session,
      feedback: {
        tone: "quiet" as const,
        text: "The same artifact continues. Watch what changes around it.",
      },
      remediation: null,
    };
    await emit(definition.id, { type: "stage_entered", payload: { stage: nextStage } });
    await persist(nextSession, nextStage, nextMax);
    navigate(`/journeys/${definition.id}/${nextStage}`);
  };

  const replayFromNotice = async () => {
    const fresh = { ...structuredClone(INITIAL_SESSION), value: definition.stages.play.start };
    await emit(definition.id, {
      type: "stage_entered",
      payload: { stage: "notice", replay: true },
    });
    await persist(fresh, "notice", 0);
    navigate(`/journeys/${definition.id}/notice`);
  };

  const playState: MysteryNumberState = {
    known: definition.stages.play.known,
    target: definition.stages.play.target,
    coefficient: 1,
    unknown: session.value,
    min: 0,
    max: definition.stages.play.max,
    locked: false,
  };

  const solveQuestion = definition.stages.solve.questions[session.solveIndex];
  const solveAnswer = solveQuestion ? (session.solveAnswers[solveQuestion.id] ?? "") : "";
  const solveState = solveQuestion ? stateForSolve(solveQuestion, solveAnswer) : playState;
  const transfer = definition.stages.transfer.question;
  const transferState = stateForSolve(transfer, session.transferAnswer);

  const artifact = useMemo(() => {
    if (stage === "play") {
      return <MysteryNumberArtifact state={playState} interactive onValueChange={(value) => void changePlayValue(value)} revealValue />;
    }
    if (stage === "predict") {
      const state = { ...playState, unknown: 9 };
      return <MysteryNumberArtifact state={state} revealValue={session.predictionChoice !== null} showBars={session.predictionChoice !== null} />;
    }
    if (stage === "solve" && solveQuestion) {
      return (
        <MysteryNumberArtifact
          state={solveState}
          equation={solveQuestion.equation}
          revealValue={session.solveCompleted.includes(solveQuestion.id)}
          showBars={(session.hintLevels[solveQuestion.id] ?? 0) >= 2}
          remediation={session.remediation}
        />
      );
    }
    if (stage === "transfer") {
      return (
        <MysteryNumberArtifact
          state={transferState}
          equation={transfer.equation}
          revealValue={session.transferComplete}
          showBars={session.transferComplete || (session.hintLevels[transfer.id] ?? 0) >= 2}
          remediation={session.remediation}
        />
      );
    }
    return (
      <MysteryNumberArtifact
        state={{ ...playState, unknown: 5 }}
        equation={
          stage === "notice"
            ? definition.stages.notice.equation
            : stage === "explain"
              ? definition.stages.explain.equation
              : undefined
        }
        revealValue={stage === "name" && session.named}
        showBars={stage !== "notice" || session.noticeCorrect}
      />
    );
  }, [stage, session, definition]);

  async function changePlayValue(value: number) {
    const currentSession = sessionRef.current;
    const direction = Math.sign(value - session.value);
    const nextValue = Math.max(
      playState.min,
      Math.min(playState.max, currentSession.value + direction),
    );
    const nextState = { ...playState, unknown: nextValue };
    const relationship = mysteryRelation(nextState);
    const nextSession: MysterySession = {
      ...currentSession,
      value: nextValue,
      playMoves: currentSession.playMoves + 1,
      playComplete: currentSession.playComplete || relationship === "equal",
      feedback:
        relationship === "equal"
          ? { tone: "success", text: definition.stages.play.success }
          : {
              tone: "quiet",
              text:
                relationship === "left-low"
                  ? `The left side is ${definition.stages.play.target - (definition.stages.play.known + nextValue)} too small.`
                  : `The left side is ${definition.stages.play.known + nextValue - definition.stages.play.target} past the target.`,
            },
    };
    sessionRef.current = nextSession;
    setSession(nextSession);
    await persist(nextSession);
    await emit(definition.id, {
      type: "widget_action",
      widgetId: "mystery-number",
      payload: {
        action: "set_unknown",
        value: nextValue,
        relationship,
        committed: true,
      },
    });
  }

  const chooseNotice = async (choice: ChoiceDefinition) => {
    const nextSession: MysterySession = {
      ...session,
      noticeChoice: choice.id,
      noticeCorrect: choice.correct,
      feedback: { tone: choice.correct ? "success" : "attention", text: choice.feedback },
    };
    await emit(definition.id, {
      type: "answer_submitted",
      payload: {
        stage: "notice",
        choice: choice.id,
        correct: choice.correct,
        evidenceDimension: "intuition",
      },
    });
    await persist(nextSession);
  };

  const choosePrediction = async (choice: ChoiceDefinition) => {
    const nextSession: MysterySession = {
      ...session,
      predictionChoice: choice.id,
      predictionCorrect: choice.correct,
      feedback: { tone: choice.correct ? "success" : "attention", text: choice.feedback },
    };
    await emit(definition.id, {
      type: "prediction_submitted",
      payload: { prediction: choice.id, correct: choice.correct },
    });
    await persist(nextSession);
  };

  const nameUnknown = async () => {
    const nextSession: MysterySession = {
      ...session,
      named: true,
      feedback: { tone: "success", text: definition.stages.name.explanation },
    };
    await emit(definition.id, {
      type: "widget_action",
      widgetId: "mystery-number",
      payload: { action: "name_unknown", term: definition.stages.name.term },
    });
    await persist(nextSession);
  };

  const requestHint = async (question: SolveDefinition) => {
    const currentLevel = session.hintLevels[question.id] ?? 0;
    const nextLevel = Math.min(question.hints.length, currentLevel + 1);
    const nextSession: MysterySession = {
      ...session,
      hintLevels: { ...session.hintLevels, [question.id]: nextLevel },
      feedback: { tone: "attention", text: question.hints[nextLevel - 1] },
    };
    await emitMany(definition.id, [
      {
        type: "hint_requested",
        questionTemplateId: question.questionFamilyId,
        payload: { level: nextLevel },
      },
      {
        type: "hint_presented",
        questionTemplateId: question.questionFamilyId,
        payload: { level: nextLevel, kind: nextLevel === 1 ? "attention" : "representation" },
      },
    ]);
    await persist(nextSession);
  };

  const submitSolve = async (event: FormEvent) => {
    event.preventDefault();
    if (!solveQuestion || session.solveCompleted.includes(solveQuestion.id)) return;
    const numeric = Number(solveAnswer);
    if (!Number.isFinite(numeric) || solveAnswer.trim() === "") {
      await persist({
        ...session,
        feedback: { tone: "attention", text: "Enter the value that belongs in the box." },
      });
      return;
    }
    const attempts = (session.solveAttempts[solveQuestion.id] ?? 0) + 1;
    const correct = numeric === solveQuestion.answer;
    const code = correct
      ? session.remediation
      : classifyMissingPartAnswer(
          numeric,
          solveQuestion.known,
          solveQuestion.target,
          solveQuestion.answer,
          attempts,
        );
    const drafts: Parameters<typeof emitMany>[1] = [
      {
        type: "answer_submitted",
        questionTemplateId: solveQuestion.questionFamilyId,
        questionSeed: `${solveQuestion.id}-v1`,
        payload: {
          answer: numeric,
          correct,
          representation: solveQuestion.representation,
          assistance: (session.hintLevels[solveQuestion.id] ?? 0) > 0 ? "hint" : null,
          resolves: correct ? code : null,
        },
      },
    ];
    if (!correct && code) {
      drafts.push(
        {
          type: "misconception_candidate",
          questionTemplateId: solveQuestion.questionFamilyId,
          payload: { code, basis: [`${solveQuestion.id}:${numeric}`] },
        },
        {
          type: "remediation_started",
          questionTemplateId: solveQuestion.questionFamilyId,
          payload: { code, representation: "magnitude" },
        },
      );
    }
    if (correct && code) {
      drafts.push({ type: "remediation_completed", payload: { code } });
    }
    await emitMany(definition.id, drafts);

    const completed = correct
      ? Array.from(new Set([...session.solveCompleted, solveQuestion.id]))
      : session.solveCompleted;
    const nextSession: MysterySession = {
      ...session,
      solveAttempts: { ...session.solveAttempts, [solveQuestion.id]: attempts },
      solveCompleted: completed,
      remediation: correct ? null : code,
      feedback: correct
        ? { tone: "success", text: solveQuestion.success }
        : {
            tone: "mismatch",
            text:
              code === "MN-COMBINE-ALL"
                ? `You combined ${solveQuestion.known} and ${solveQuestion.target}. But ${solveQuestion.target} is already the whole.`
                : code === "MN-DIFFERENCE-BACKWARDS"
                  ? "The distance went in the wrong direction. Test the result in the original statement."
                  : code === "MN-IGNORE-RELATION"
                    ? "Pause the guesses. Build the whole left side and compare it with the target."
                    : `That makes a different relationship. Test ${numeric} in the complete statement.`,
          },
    };
    await persist(nextSession);
  };

  const nextSolve = async () => {
    const nextIndex = Math.min(definition.stages.solve.questions.length - 1, session.solveIndex + 1);
    await persist({
      ...session,
      solveIndex: nextIndex,
      remediation: null,
      feedback: { tone: "quiet", text: "The surface changed. The missing-part relationship did not." },
    });
  };

  const chooseExplanation = async (choice: ChoiceDefinition) => {
    const nextSession: MysterySession = {
      ...session,
      explanationChoice: choice.id,
      explanationCorrect: choice.correct,
      feedback: { tone: choice.correct ? "success" : "attention", text: choice.feedback },
    };
    await emit(definition.id, {
      type: "explanation_submitted",
      payload: { choice: choice.id, correct: choice.correct },
    });
    await persist(nextSession);
  };

  const submitTransfer = async (event: FormEvent) => {
    event.preventDefault();
    const numeric = Number(session.transferAnswer);
    if (!Number.isFinite(numeric) || session.transferAnswer.trim() === "") {
      await persist({
        ...session,
        feedback: { tone: "attention", text: "Enter the value shared by both boxes." },
      });
      return;
    }
    const attempts = session.transferAttempts + 1;
    const correct = numeric === transfer.answer;
    const code = correct
      ? session.remediation
      : numeric === transfer.target * 2
        ? "MN-COMBINE-ALL"
        : attempts >= 3
          ? "MN-BOX-AS-ANYTHING"
          : null;
    const drafts: Parameters<typeof emitMany>[1] = [
      {
        type: "transfer_gate_completed",
        questionTemplateId: transfer.questionFamilyId,
        questionSeed: "transfer-linked-v1",
        payload: {
          answer: numeric,
          correct,
          representation: "linked-objects",
        },
      },
    ];
    if (!correct && code) {
      drafts.push({
        type: "misconception_candidate",
        payload: { code, basis: [`transfer:${numeric}`] },
      });
    }
    if (correct) {
      const dueAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      drafts.push({
        type: "review_scheduled",
        payload: {
          dueAt,
          variantId: "reversed-equation",
          reason: "Reconstruct the missing-part relationship after a pause.",
        },
      });
    }
    await emitMany(definition.id, drafts);
    const nextSession: MysterySession = {
      ...session,
      transferAttempts: attempts,
      transferComplete: correct,
      completed: correct,
      remediation: correct ? null : code,
      feedback: correct
        ? { tone: "success", text: transfer.success }
        : {
            tone: "mismatch",
            text: code === "MN-COMBINE-ALL"
              ? "The two boxes together make 14. Do not double the whole."
              : "Both boxes share one binding. Split 14 into two equal parts.",
          },
    };
    await persist(nextSession);
  };

  const renderControls = () => {
    if (stage === "notice") {
      return (
        <>
          <div className="stage-question">
            <h2>{definition.stages.notice.question}</h2>
            <ChoiceList
              choices={definition.stages.notice.choices}
              selected={session.noticeChoice}
              locked={session.noticeCorrect}
              onChoose={(choice) => void chooseNotice(choice)}
            />
          </div>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
          <div className="stage-actions"><button className="btn btn-primary" type="button" disabled={!session.noticeCorrect} onClick={() => void continueForward()}>Continue</button></div>
        </>
      );
    }
    if (stage === "play") {
      return (
        <>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
          <div className="stage-actions"><button className="btn btn-primary" type="button" disabled={!session.playComplete} onClick={() => void continueForward()}>Continue</button></div>
        </>
      );
    }
    if (stage === "predict") {
      return (
        <>
          <div className="stage-question">
            <h2>{definition.stages.predict.question}</h2>
            <ChoiceList choices={definition.stages.predict.choices} selected={session.predictionChoice} locked={session.predictionCorrect} onChoose={(choice) => void choosePrediction(choice)} />
          </div>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
          <div className="stage-actions"><button className="btn btn-primary" type="button" disabled={!session.predictionCorrect} onClick={() => void continueForward()}>Continue</button></div>
        </>
      );
    }
    if (stage === "name") {
      return (
        <>
          <div className="name-reveal">
            <span className="name-symbol">□</span>
            <span className="name-arrow" aria-hidden="true">→</span>
            <strong>{session.named ? definition.stages.name.term : "?"}</strong>
          </div>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
          <div className="stage-actions">
            {session.named ? (
              <button className="btn btn-primary" type="button" onClick={() => void continueForward()}>Continue</button>
            ) : (
              <button className="btn btn-primary" type="button" onClick={() => void nameUnknown()}>Name the hidden value</button>
            )}
          </div>
        </>
      );
    }
    if (stage === "solve" && solveQuestion) {
      const solved = session.solveCompleted.includes(solveQuestion.id);
      const allSolved = session.solveCompleted.length === definition.stages.solve.questions.length;
      return (
        <>
          <form className="solve-form" onSubmit={(event) => void submitSolve(event)}>
            <div>
              <span className="tag tag-accent">Puzzle {session.solveIndex + 1} of {definition.stages.solve.questions.length}</span>
              {solveQuestion.story ? <p className="solve-story">{solveQuestion.story}</p> : null}
              <h2>{solveQuestion.prompt}</h2>
            </div>
            <label className="answer-field">
              <span>Your value</span>
              <input
                type="number"
                inputMode="numeric"
                value={solveAnswer}
                disabled={solved}
                onChange={(event) =>
                  setSession((current) => ({
                    ...current,
                    solveAnswers: { ...current.solveAnswers, [solveQuestion.id]: event.target.value },
                  }))
                }
              />
            </label>
            {!solved ? (
              <div className="inline-actions">
                <button className="btn btn-ghost" type="button" disabled={(session.hintLevels[solveQuestion.id] ?? 0) >= solveQuestion.hints.length} onClick={() => void requestHint(solveQuestion)}>Use a clue</button>
                <button className="btn btn-primary" type="submit">Test the relationship</button>
              </div>
            ) : null}
          </form>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
          {solved ? (
            <div className="stage-actions">
              {allSolved ? (
                <button className="btn btn-primary" type="button" onClick={() => void continueForward()}>Continue</button>
              ) : (
                <button className="btn btn-primary" type="button" onClick={() => void nextSolve()}>Next puzzle</button>
              )}
            </div>
          ) : null}
        </>
      );
    }
    if (stage === "explain") {
      return (
        <>
          <div className="stage-question">
            <h2>{definition.stages.explain.question}</h2>
            <ChoiceList choices={definition.stages.explain.choices} selected={session.explanationChoice} locked={session.explanationCorrect} onChoose={(choice) => void chooseExplanation(choice)} />
          </div>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
          <div className="stage-actions"><button className="btn btn-primary" type="button" disabled={!session.explanationCorrect} onClick={() => void continueForward()}>To the far door</button></div>
        </>
      );
    }
    if (stage === "transfer") {
      if (session.completed) {
        const visibleState = projection?.concepts[definition.id]?.visibleState ?? "working";
        return (
          <div className="completion-panel">
            <span className="completion-star" aria-hidden="true">✦</span>
            <p className="page-kicker">JOURNEY COMPLETE</p>
            <h2>{visibleState === "strong" ? "The Hidden Grove is mapped." : "The far door is open."}</h2>
            <div className="takeaway-row">
              <span><strong>NOTICE</strong>The whole pins the part.</span>
              <span><strong>UNDO</strong>Subtraction retraces addition.</span>
              <span><strong>TRANSFER</strong>Linked boxes share one value.</span>
            </div>
            <p>{visibleState === "strong" ? "A fresh mystery will be waiting tomorrow." : "You opened the door. Return to Solve when you want to try it without clues."}</p>
            <div className="completion-actions">
              {visibleState !== "strong" ? <button className="btn btn-secondary" type="button" onClick={() => void goToStage("solve")}>Revisit Solve</button> : null}
              <Link className="btn btn-primary" to="/atlas">Return to the Atlas</Link>
            </div>
          </div>
        );
      }
      return (
        <>
          <form className="solve-form" onSubmit={(event) => void submitTransfer(event)}>
            <div>
              <span className="tag tag-accent">New shape · same relationship</span>
              {transfer.story ? <p className="solve-story">{transfer.story}</p> : null}
              <h2>{transfer.prompt}</h2>
            </div>
            <label className="answer-field"><span>Each box holds</span><input type="number" inputMode="numeric" value={session.transferAnswer} onChange={(event) => setSession((current) => ({ ...current, transferAnswer: event.target.value }))} /></label>
            <div className="inline-actions">
              <button className="btn btn-ghost" type="button" disabled={(session.hintLevels[transfer.id] ?? 0) >= transfer.hints.length} onClick={() => void requestHint(transfer)}>Use a clue</button>
              <button className="btn btn-primary" type="submit">Open the far door</button>
            </div>
          </form>
          <FeedbackLane tone={session.feedback.tone}>{session.feedback.text}</FeedbackLane>
        </>
      );
    }
    return null;
  };

  const instruction = definition.stages[stage].instruction;

  return (
    <main className="journey-page">
      <section className="journey-shell">
        <header className="journey-header">
          <div className="journey-heading-row">
            <div>
              <p className="page-kicker">JOURNEY 01 · {definition.world}</p>
              <h1>{definition.title}</h1>
            </div>
            <div className="journey-header-actions">
              <Link className="btn btn-ghost" to="/atlas">← Atlas</Link>
              <button className="btn btn-ghost" type="button" onClick={() => void replayFromNotice()}>↺ Replay from Notice</button>
            </div>
          </div>
          <StageRail current={stage} maxReached={maxStage} onSelect={(nextStage) => void goToStage(nextStage)} />
        </header>

        <section className="stage-card card">
          <p className="stage-instruction">{instruction}</p>
          {artifact}
          <div className="stage-controls">{renderControls()}</div>
        </section>

      </section>
    </main>
  );
}
